import React from "react";
import { useIntl } from "react-intl";

import { Checkbox, DateInput, Section, SelectInput, SubSection } from "~/core";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { ACTIVE_YN } from "~/core/picklist";
import { messages } from "~/reports/i18n-messages";
import { model } from "~/reports/data";
import { getReportDataByReportType, useReportCropOptions } from "~/reports/data/selectors";
import { IS_SELECTABLE } from "~/utils/products/use-product-list";

import { FieldAPI } from "@ai360/core";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { getFieldsPerPageOptions } from "../utils";
import { setReportDataValue } from "../data/actions";

function ReportBookReportOptions() {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const onInputChange = (key: string, value: any) =>
        dispatch(
            setReportDataValue({ key, value, reportType: ReportBookReportOptions.reportName })
        );
    const reportData = useAppSelector(
        getReportDataByReportType(ReportBookReportOptions.reportName)
    );
    const crops = useReportCropOptions();
    const selectedCrop = crops.find(
        (crop) => crop.value.cropGuid === reportData[model.PROPS_CROP_GUID]
    );

    const croppingSeasonDisabled = !!(
        reportData[model.PROPS_START_DATE] || reportData[model.PROPS_END_DATE]
    );

    return (
        <Section className="report-options" testId="report-book-options-section">
            <SubSection testId="report-book-season-and-crop-subsection">
                <CroppingSeason
                    disabled={croppingSeasonDisabled}
                    required={!croppingSeasonDisabled}
                    reportType={ReportBookReportOptions.reportName}
                />
                <SelectInput<FieldAPI.ICrop>
                    disabled={false}
                    optionIsHiddenKey={IS_SELECTABLE}
                    placeholderText={formatMessage(messages.crop)}
                    options={crops}
                    onChange={(value) => {
                        onInputChange(model.PROPS_CROP_GUID, value ? value.cropGuid : null);
                    }}
                    value={selectedCrop?.value as FieldAPI.ICrop}
                    testId="report-book-crop-select"
                />
            </SubSection>
            <SubSection testId="report-book-dates-subsection">
                <DateInput
                    disabled={false}
                    placeholderText={formatMessage(messages.startDate)}
                    onChange={(value) =>
                        onInputChange(model.PROPS_START_DATE, value ? value : null)
                    }
                    timeFormat={false}
                    value={reportData[model.PROPS_START_DATE]}
                    testId="report-book-start-date-date"
                />
                <DateInput
                    disabled={false}
                    placeholderText={formatMessage(messages.endDate)}
                    onChange={(value) => onInputChange(model.PROPS_END_DATE, value ? value : null)}
                    timeFormat={false}
                    value={reportData[model.PROPS_END_DATE]}
                    testId="report-book-end-date-date"
                />
            </SubSection>
            <SubSection testId="report-book-group-by-subsection">
                <SelectInput
                    required
                    clearable={false}
                    optionIsHiddenKey={ACTIVE_YN}
                    containerClassNames={["display-options-list"]}
                    placeholderText={formatMessage(messages.reportBookGroupByOption)}
                    options={getFieldsPerPageOptions()}
                    onChange={(value) => {
                        if (value != "field") {
                            onInputChange(model.PROPS_INDIVIDUAL_RB, false);
                        }
                        onInputChange(model.PROPS_REPORT_BOOK_GROUP_BY, value);
                    }}
                    value={reportData[model.PROPS_REPORT_BOOK_GROUP_BY]}
                    testId="report-book-group-by-select"
                />
            </SubSection>
            <SubSection testId="report-book-individual-report-book-subsection">
                <Checkbox
                    className={""}
                    disabled={reportData[model.PROPS_REPORT_BOOK_GROUP_BY] != "field"}
                    label={formatMessage(messages.individualReportBook, {})}
                    onChange={(e, value) => onInputChange(model.PROPS_INDIVIDUAL_RB, value)}
                    value={reportData[model.PROPS_INDIVIDUAL_RB]}
                    testId="report-book-individual-report-book-checkbox"
                />
            </SubSection>
        </Section>
    );
}

ReportBookReportOptions.defaultReportOptions = {
    [model.PROPS_INDIVIDUAL_RB]: false,
    [model.PROPS_REPORT_BOOK_GROUP_BY]: "grower",
};

ReportBookReportOptions.reportPreferences = [];

ReportBookReportOptions.reportName = "ReportBook";

export default ReportBookReportOptions;
